/**
 * Money model: every stored amount (prices, costs, order totals) is in US dollar CENTS.
 * Pesos are derived for display and for Mercado Pago: USD × dólar blue × (1 + 5%), rounded up to $10,
 * unless an admin fixed the variant's peso price (product_variants.price_ars), which then wins.
 */

export type DisplayCurrency = "USD" | "ARS";

/** Surcharge applied when showing / charging in pesos. */
export const ARS_SURCHARGE = 0.05;

/** Pesos are shown and charged rounded up to this step. */
export const ARS_STEP = 10;

/** Default floor for catalog prices: US$0.50. */
export const MIN_PRICE_CENTS = 50;

/** Whole ARS for an amount in USD cents at `rate` ARS per USD (surcharge included, rounded up to $10). */
export function usdCentsToArs(cents: number, rate: number): number {
  if (!Number.isFinite(cents) || cents <= 0) return 0;
  // Integer math with an epsilon so 100 × 1450 × 1.1 does not become 159500.00000001.
  const ars = (cents * rate * (1 + ARS_SURCHARGE)) / 100;
  return Math.ceil(ars / ARS_STEP - 1e-9) * ARS_STEP;
}

/**
 * Mercado Pago's fee (%), setting mp_fee_pct. Peso prices are built around the bank-transfer price:
 * paying with Mercado Pago (card) costs the transfer price grossed up by this fee, so the shop nets
 * the same either way (e.g. $6.860 charged, $281,26 fee → $6.578,74 = 4,1%).
 */
export const DEFAULT_MP_FEE_PCT = 4.1;

const hasFixed = (fixed: number | null | undefined): fixed is number => fixed != null && fixed > 0;

/** Transfer price grossed up by the fee, rounded up to $10. */
export function cardFromTransferArs(transferArs: number, mpFeePct: number): number {
  return Math.ceil((transferArs * 100) / (100 - mpFeePct) / ARS_STEP - 1e-9) * ARS_STEP;
}

/**
 * Whole ARS per unit by bank transfer: the admin's fixed peso price (that is a transfer price), or the
 * converted price (dólar + 5%, the Mercado Pago price) minus the fee.
 */
export function transferUnitArs(cents: number, rate: number, fixedTransferArs: number | null | undefined, mpFeePct: number): number {
  if (hasFixed(fixedTransferArs)) return fixedTransferArs;
  return transferFromCardArs(usdCentsToArs(cents, rate), mpFeePct);
}

/** The transfer price that nets the same as a card price after the fee (rounded down, so grossing it up gives the card price back). */
export function transferFromCardArs(cardArs: number, mpFeePct: number): number {
  return Math.floor((cardArs * (100 - mpFeePct)) / 100 + 1e-9);
}

/** Whole ARS per unit with Mercado Pago (card): the fixed transfer price plus the fee, or the converted price. */
export function cardUnitArs(cents: number, rate: number, fixedTransferArs: number | null | undefined, mpFeePct: number): number {
  return hasFixed(fixedTransferArs) ? cardFromTransferArs(fixedTransferArs, mpFeePct) : usdCentsToArs(cents, rate);
}

/** Pesos after a % discount (bank transfer), whole pesos off rounded down, as the charge does. */
export function discountedArs(ars: number, pct: number): number {
  return ars - Math.floor((ars * pct) / 100);
}

/** Inverse, for turning a peso amount typed in a filter into USD cents (floor/ceil chosen by caller). */
export function arsToUsdCents(ars: number, rate: number, round: "floor" | "ceil" = "floor"): number {
  const cents = (ars * 100) / (rate * (1 + ARS_SURCHARGE));
  return round === "floor" ? Math.floor(cents + 1e-9) : Math.ceil(cents - 1e-9);
}

const formatters = {
  USD: {
    es: new Intl.NumberFormat("es-AR", { style: "currency", currency: "USD", currencyDisplay: "code", minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    en: new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  },
  ARS: {
    es: new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }),
    en: new Intl.NumberFormat("en-US", { style: "currency", currency: "ARS", currencyDisplay: "code", maximumFractionDigits: 0 }),
  },
} as const;

/** "US$ 12,50" / "$12.50" for USD cents. */
export function formatUsd(cents: number, locale: "es" | "en" = "es"): string {
  const s = formatters.USD[locale].format(cents / 100);
  return locale === "es" ? s.replace(/^USD\s?/, "US$ ") : s;
}

/** "$ 18.500" / "ARS 18,500" for whole pesos. */
export function formatArs(pesos: number, locale: "es" | "en" = "es"): string {
  return formatters.ARS[locale].format(pesos);
}

/** Parses "12.50", "12,50", "12" (dollars, as typed in the admin) into cents; null if empty, NaN if invalid. */
export function parseUsdInput(value: string): number | null {
  const v = value.trim().replace(/^US\$\s*|^\$\s*/i, "");
  if (v === "") return null;
  if (!/^\d{1,7}([.,]\d{1,2})?$/.test(v)) return NaN;
  const [whole, frac = ""] = v.split(/[.,]/);
  return Number(whole) * 100 + Number(frac.padEnd(2, "0"));
}

/** Cents → "12.50" for admin inputs. */
export function centsToInput(cents: number | null | undefined): string {
  return cents == null ? "" : (cents / 100).toFixed(2);
}
